/**
 * Teaching a feature the first time somebody touches it.
 *
 * Separate from `tutorial.ts`, which is one conversation with Control about how
 * to fly. This is the rest of the game, explained by the ship, at the moment
 * each part of it first becomes true.
 *
 * ── Said twice, differently ─────────────────────────────────────────────────
 *
 * **Loudly, once.** `lines` is an on-screen interruption the first time a
 * feature is used — the only time the game will explain that system unprompted,
 * so it can afford the room: what the thing is, what it is for, and which key.
 *
 * **Quietly, always.** `brief` is one sentence in the ship's console log every
 * time the feature is used afterwards. Not a lesson — a reminder that happens
 * to teach anybody who skipped the first one, or came back a month later having
 * forgotten which key raises the module.
 *
 * The split is what makes the loud half affordable. An interruption is only
 * tolerable if it happens once and a log line is only useful if it is short, so
 * each is allowed to be the thing it is good at and neither carries the other's
 * job.
 *
 * ── Why the text lives here ─────────────────────────────────────────────────
 *
 * Beside the rules it describes rather than in the component that draws it. Both
 * halves read the same entry, so the loud version and the quiet one cannot drift
 * into saying different things about the same feature — and a key rebound in
 * `input.ts` moves in both at once, because both ask for it by action.
 */
import { keyLabel, pairLabel } from './input.js';
/**
 * Every lesson, by id.
 *
 * Ids are stored on the account, so **renaming one re-teaches it** to everybody
 * who had already seen it. They are content-free on purpose — `jump-targets`
 * rather than `jump-targets-v2` — so the text can be rewritten freely without
 * anybody being taught it twice.
 */
export const LESSONS = {
    'jump-targets': {
        id: 'jump-targets',
        title: 'Jump module',
        lines: [
            'The jump module plots crossings between systems. Activating it shows every ' +
                'destination within reach as a marker, with its name and its distance.',
            'A marker you can actually reach is drawn bright. A dim one is out of range ' +
                'for this drive — a bigger engine reaches further — and one marked for a ' +
                'core needs a warp core in the hold to cross between clusters.',
            `Put a destination under your reticle and hold [${keyLabel('dock')}] to commit. ` +
                'Once locked you cannot cancel the jump, ensure a clear path. ',
            `The module needs the main drive and the gear up. [${keyLabel('jump')}] stows it again.`,
        ],
        brief: 'Jump Module: online || Jump markers active',
        briefOff: 'Jump Module: deactivated',
    },
    // ── Money and loss ────────────────────────────────────────────────────────
    // First, because these are the ones that cost you rather than confuse you.
    'carried-vs-banked': {
        id: 'carried-vs-banked',
        title: 'Carried and banked',
        lines: [
            'Linelits are in one of two places, and the difference is the whole of it. ' +
                'What is carried is aboard this ship — spendable anywhere, and lost in ' +
                'full if the hull is.',
            'What is banked sits in a station vault. It is reachable from the vault ' +
                'counter at any station, and nothing that happens out here can touch it. ' +
                'It is the only thing that survives a wreck.',
        ],
        brief: 'Linelits aboard are lost with the hull. Bank what you are not about to spend.',
    },
    'banking-cap': {
        id: 'banking-cap',
        title: 'Vault capacity',
        lines: [
            'Your vaults maximum capacity is dermined by the value of your ship. ' +
                'if your ship is lost, any Linelits you have vaulted will remain avalible to you. ',
            'if your ship value goes below your valulted amount, you will not be able to deposit ' +
                'further Linelits.',
            'The limit rises with the ship: a larger hull, better components, or ' +
                'cargo in the hold all count towards the max value you can vault.',
        ],
        brief: 'The vault holds only what the ship is worth — the rest stays aboard.',
    },
    'hull-lost': {
        id: 'hull-lost',
        title: 'What a wreck costs',
        lines: [
            'The hull is gone and everything aboard went with it: linelits carried, ' +
                'cargo, and any delivery part-way through its run.',
            'The blueprint goes too. What you are handed back is a starter — the same ' +
                'ship a new pilot gets — because a design that survived would make a ' +
                'wreck a formality.',
            'What survives is what was in the vault. Banked linelits are untouched, ' +
                'and they are the head start you rebuild on. That is what the trip to a ' +
                'vault counter buys.',
        ],
        brief: 'N/A',
    },
    // ── Flying it ─────────────────────────────────────────────────────────────
    thrusters: {
        id: 'thrusters',
        title: 'Thruster sets',
        lines: [
            'Two sets of thrusters, and they fly like different ships. The main drive ' +
                'holds a throttle: wind it up and the ship keeps working toward that ' +
                'setting until you change it, which is what crossing a system wants.',
            `The precision set does not hold anything. [${pairLabel('throttleUp', 'throttleDown')}] ` +
                'is a push — let go and you come to rest — and it strafes twice as hard. ' +
                'That is what puts a hull on a plate.',
            `Sideways is its own control on either set: [${pairLabel('strafeLeft', 'strafeRight')}] ` +
                `slide you across, [${pairLabel('up', 'down')}] lift and drop you, and ` +
                `[${pairLabel('rollLeft', 'rollRight')}] roll you level. Moving without ` +
                'turning is how you line anything up.',
            'Both fade with speed. Below about a quarter of your top speed you have ' +
                'every bit of sideways authority; near the top you have almost none — so ' +
                'slow down first, then nudge.',
        ],
        brief: 'Thrust: precision || speed reduced, strafe boosted.',
        briefOff: 'Thrust: main',
    },
    // ── Hazards ───────────────────────────────────────────────────────────────
    heat: {
        id: 'heat',
        title: 'Hull temperature',
        lines: [
            'Solar radiation is cooking the plating. Inside a star\'s reach the hull takes ' +
                'damage continuously.',
            'Maintain a safe distance from heat emmiting bodies, ' +
                'once outside the reach, heat will reduce.',
            'Be aware of this when jumping between systems, the solar signiture informs the jump ' +
                'drive to disengage, but may leave you in range of the star upon arrival.',
        ],
        brief: 'Hull temperature rising: avoid heat emmiting bodies.',
    },
    asteroid: {
        id: 'asteroid',
        title: 'Asteroids',
        lines: [
            'Rocks are solid. A belt is scenery you can hit — anything out there will ' +
                'dent the hull, and what it costs scales with how fast you met it.',
            `Belts are crossed slowly. [${keyLabel('thrusterSet')}] for the precision ` +
                'set gives you the authority to thread one; the main drive gives you the ' +
                'speed to regret it.',
            'Nothing steers around them for you. Slow down, or go over.',
        ],
        brief: 'Asteroids are solid — a belt is crossed slowly, or not at all.',
    },
    'mission-deadline': {
        id: 'mission-deadline',
        title: 'Delivery deadlines',
        lines: [
            'A delivery is a crate in your hold with a clock on it. The fee is agreed ' +
                'now and paid on arrival, by the station that wanted it.',
            'The deadline is total. An expired crate pays nothing at all — not a ' +
                'reduced fee, nothing — and no trader will buy it off you, so it is dead ' +
                'weight in the hold until you dump it.',
            'The clock started when you accepted, not when the job appeared. Check the ' +
                'run is one you can make: the board shows the time left, and the chart ' +
                'shows how far it is.',
        ],
        brief: 'Delivery aboard. An expired crate pays nothing, so watch the clock.',
    },
};
/**
 * Carrying this much is worth being told about.
 *
 * Beside the text that explains it rather than in the view that watches for it,
 * because the number and the sentence are the same decision: high enough that a
 * player who has only ever sold one crate is not lectured, low enough to land
 * well before a starter's vault is full — which is around 197, so this is a
 * little over two thirds of a first ship's worth.
 */
export const CARRIED_AT_RISK = 140;
export function lessonById(id) {
    return LESSONS[id];
}
/** Every lesson id, for the switch that marks them all seen. */
export function allLessonIds() {
    return Object.keys(LESSONS);
}
//# sourceMappingURL=lessons.js.map